/**
 * Jurídico: alertas automáticos de prazo, obrigações contratuais recorrentes, exportação e
 * relatórios (ADR 0028). Os prazos vêm das datas cadastradas; nenhum prazo legal é calculado.
 */
import { z } from 'zod';
import { exportInput, type ExportInput } from './export.ts';

/** De onde vem o prazo de cada alerta. */
export const ALERT_SOURCES = [
  'contract_end',
  'contract_notice',
  'contract_adjustment',
  'ip_expiry',
  'ip_renewal',
  'matter_due',
  'action_due',
  'request_deadline',
] as const;
export type AlertSource = (typeof ALERT_SOURCES)[number];
/** Tratamento: aberto, em tratamento, concluído ou justificado (com motivo). */
export const ALERT_STATUSES = ['open', 'in_treatment', 'done', 'justified'] as const;
/** Marcos iniciais (dias antes do prazo), configuráveis pelo gestor do catálogo jurídico. */
export const DEFAULT_ALERT_MILESTONES = [90, 60, 30] as const;

/**
 * Prazos externos informados nas solicitações (ADR 0063): onde ficam no formulário de cada
 * modalidade. Não são SLA: nunca são calculados nem suspensos; só geram alertas.
 */
export const REQUEST_DEADLINE_FIELDS: Record<
  string,
  { flag: string; date: string; origin: string | null }
> = {
  F33: { flag: 'possui_prazo', date: 'prazo_devolucao', origin: null },
  F34: { flag: 'possui_prazo', date: 'prazo_resposta', origin: 'prazo_origem' },
};
/** Marcos iniciais (dias antes) dos prazos externos, configuráveis pelo gestor do catálogo. */
export const DEFAULT_REQUEST_DEADLINE_MILESTONES = [7, 3, 1] as const;

/** Prazo externo informado na solicitação (data e origem), ou null quando não há. */
export function requestDeadline(
  serviceCode: string,
  fields: Record<string, unknown> | null | undefined,
): { dueDate: string; origin: string | null } | null {
  const spec = REQUEST_DEADLINE_FIELDS[serviceCode];
  if (!spec || !fields || fields[spec.flag] !== true) return null;
  const due = fields[spec.date];
  if (typeof due !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(due)) return null;
  const origin = spec.origin ? fields[spec.origin] : null;
  return {
    dueDate: due,
    origin: typeof origin === 'string' && origin.trim() ? origin.trim() : null,
  };
}

export const OBLIGATION_KINDS = ['payment', 'delivery', 'report', 'other'] as const;
export const OBLIGATION_PERIODICITIES = [
  'one_time',
  'monthly',
  'quarterly',
  'semiannual',
  'yearly',
] as const;
export const OBLIGATION_STATUSES = ['active', 'suspended', 'closed'] as const;
/** Tipo da ação da agenda gerada por cada tipo de obrigação. */
export const OBLIGATION_ACTION_KIND: Record<(typeof OBLIGATION_KINDS)[number], string> = {
  payment: 'payment',
  delivery: 'delivery',
  report: 'report',
  other: 'follow_up',
};

/**
 * Marco que vale hoje para um prazo: o menor marco que já foi alcançado (ex.: faltam 45 dias,
 * marcos 90/60/30 → 60); 0 quando o prazo já passou; null quando ainda está longe.
 */
export function alertMilestone(
  dueDate: string,
  today: string,
  milestones: readonly number[],
): number | null {
  const days = Math.round(
    (Date.parse(`${dueDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  if (days < 0) return 0;
  const reached = milestones.filter((m) => days <= m);
  return reached.length > 0 ? Math.min(...reached) : null;
}

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const legalObligationInput = z
  .object({
    contractId: z.uuid(),
    kind: z.enum(OBLIGATION_KINDS),
    title: z.string().trim().min(1).max(300),
    description: text(10000),
    ownerId: z.uuid('Escolha o responsável'),
    periodicity: z.enum(OBLIGATION_PERIODICITIES),
    nextDueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe o próximo vencimento'),
    endDate: day,
    amount: z
      .string()
      .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números com ponto (ex.: 1500.00)')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    currency: z
      .string()
      .regex(/^([A-Z]{3})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    status: z.enum(OBLIGATION_STATUSES).default('active'),
    /** Entrega de obra (A4): a obra entregue, entre as ligadas ao contrato. */
    workId: z.uuid().nullable().optional(),
  })
  .refine((v) => !v.workId || v.kind === 'delivery', {
    path: ['workId'],
    message: 'A obra só vale para obrigação de entrega',
  })
  .refine((v) => (v.amount == null) === (v.currency == null), {
    path: ['currency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => !v.endDate || v.endDate >= v.nextDueDate, {
    path: ['endDate'],
    message: 'O fim não pode ser antes do próximo vencimento',
  });
export type LegalObligationInput = z.infer<typeof legalObligationInput>;

/** Aceite de uma entrega de obra (A4): observação opcional; encerra a entrega. */
export const legalDeliveryAcceptInput = z.object({ note: text(2000) });

export const legalAlertHandleInput = z
  .object({
    status: z.enum(['in_treatment', 'done', 'justified']),
    note: text(2000),
  })
  .refine((v) => v.status !== 'justified' || !!v.note, {
    path: ['note'],
    message: 'Informe a justificativa',
  });

export const legalAlertSettingInput = z.object({
  milestones: z
    .array(z.number().int().min(1).max(365))
    .min(1)
    .max(6)
    .transform((v) => [...new Set(v)].sort((a, b) => b - a)),
  emailEnabled: z.boolean(),
  requestMilestones: z
    .array(z.number().int().min(1).max(90))
    .min(1)
    .max(6)
    .transform((v) => [...new Set(v)].sort((a, b) => b - a))
    // Sem o campo, os marcos dos prazos externos continuam como estão.
    .optional(),
});

/** Exportação em Excel do Jurídico: o formato comum de todas as listas (ADR 0087). */
export const legalExportInput = exportInput;
export type LegalExportInput = ExportInput;
